/*  Anti-Snoring Guard product page — CMS-driven marketing page.
    ────────────────────────────────────────────────────────────────
    iter100.141 — Thin caller over `product-page/sections.jsx`. Every
    hero/section string now lives in `db.cms_content` under page slug
    `product_serene` and is editable from Admin → Landing Page.

    Naming rule:
      • Sub-menu / navigation label stays generic: "Anti-Snoring Guard".
      • On this PAGE the brand identity "Invisique Serene" is used
        (no ™ — user removed all ™ marks earlier).
*/
import React, { useEffect } from "react";
import { useProductPageCms } from "@/lib/useProductPageCms";
import {
    Hero, TrustBar, PainPoint, Benefits, Science, Journey, ComparisonTable,
    Testimonials, DoctorCredibility, Faq, RelatedProducts, FinalCta,
} from "@/components/product-page/sections";
import GuidedCallbackForm from "@/components/site/GuidedCallbackForm";
import SeoHead from "@/components/site/SeoHead";


const BRAND = "Invisique Serene";
const CMS_PAGE = "product_serene";
const TEST_PREFIX = "serene";
const PRODUCT_SLUG = "anti-snoring";


/* Hero image — single owned asset (see Night Guard for rationale). */
const HERO_GALLERY = [
    {
        label: "Product",
        url: "https://customer-assets.emergentagent.com/job_code-fetch-32/artifacts/170sfvrr_Antis_Snoring_Guard.png",
        alt: "Invisique Serene anti-snoring guard — improved airflow, custom-fit",
    },
];

const COMPARISON_HEADERS = [
    "Feature", BRAND, "CPAP Machine", "Nasal Strips", "Medication", "Do Nothing",
];
const COMPARISON_ROWS = [
    ["Reduces Snoring", "yes", "yes", "no-Minimal", "no", "no"],
    ["Doctor Supervised", "yes", "warn-Varies", "no", "warn", "no"],
    ["Comfortable Nightly Wear", "yes", "no-Bulky & loud", "warn-Some", "warn-Some", "na"],
    ["Medical-Grade", "yes", "yes", "warn-Varies", "yes", "na"],
    ["Portable & Discreet", "yes", "no-Loud & bulky", "yes", "yes", "na"],
    ["Custom-Fit", "yes", "no-One-size-fits-most", "no", "no", "na"],
    ["Works While Sleeping", "yes", "yes", "warn-Some", "yes", "no"],
    ["Side Effects", "yes-Minimal", "warn-Claustrophobia", "warn-Skin irritation", "warn-Varies", "no-Ongoing risk"],
];

const RELATED = [
    { title: "Invisique Secure", subtitle: "Sleep protected", body: "Custom night guard for teeth grinding.", href: "/mouthguards-and-retainer/night-guard", icon: "Moon", tint: "from-indigo-100 to-slate-100", iconTint: "text-indigo-600" },
    { title: "Invisique Apex", subtitle: "Play hard, protect harder", body: "For athletes who need protection.", href: "/mouthguards-and-retainer/sports-guard", icon: "ShieldStar", tint: "from-amber-100 to-rose-100", iconTint: "text-amber-600" },
    { title: "Invisique Infinite", subtitle: "Lifetime hold", body: "Keep the smile you worked hard for.", href: "/mouthguards-and-retainer/retainer", icon: "Star", tint: "from-emerald-100 to-teal-100", iconTint: "text-emerald-600" },
];


export default function InvisiqueSerenePage() {
    const { t, arr, loading } = useProductPageCms(CMS_PAGE);

    useEffect(() => {
        // SeoHead below drives document.title from CMS.
    }, []);

    if (loading) {
        return <div className="min-h-[60vh] bg-white" aria-hidden="true" data-testid="serene-loading" />;
    }

    return (
        <div className="bg-white" data-testid="invisique-serene-page">
            <SeoHead
                page={CMS_PAGE}
                fallbackTitle={`${BRAND} · Anti-Snoring Guard · Invisique`}
                fallbackDescription="Custom mandibular advancement device to reduce snoring. Doctor-fitted."
            />
            <Hero
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                gallery={HERO_GALLERY}
                halosTint="sky"
                captionIcon="Bed"
                credibilityIcon="Star"
            />
            <TrustBar t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <PainPoint t={t} arr={arr} testPrefix={TEST_PREFIX} layout="rows" />
            <Benefits t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Science t={t} testPrefix={TEST_PREFIX} />
            <Journey t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-secondary/30" />
            <ComparisonTable
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                headers={COMPARISON_HEADERS}
                rows={COMPARISON_ROWS}
                bg="bg-white"
            />
            <Testimonials t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-secondary/30" scope="product_serene" />
            <DoctorCredibility t={t} arr={arr} testPrefix={TEST_PREFIX} gradient="from-primary/[0.06] via-white to-sky-50" />
            <Faq t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <GuidedCallbackForm
                productSlug={PRODUCT_SLUG}
                defaultInterest={PRODUCT_SLUG}
                themeTint="sky"
                testPrefix={TEST_PREFIX}
                extraSelects={[
                    {
                        key: "primary_concern",
                        label: "My primary concern is",
                        options: [
                            "Sleep quality",
                            "Snoring",
                            "Partner's sleep",
                            "Sleep apnea concerns",
                            "Not sure",
                        ],
                    },
                ]}
            />
            <RelatedProducts
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                items={RELATED}
            />
            <FinalCta
                t={t}
                testPrefix={TEST_PREFIX}
                onCallbackClick={() => document.getElementById("guided-callback-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            />
        </div>
    );
}
